import React, { useState, useMemo } from 'react';
import { useCustomers } from '../hooks/useCustomers';
import { useOrganizations } from '../hooks/useOrganizations';
import CustomerForm from '../components/customers/CustomerForm';
import { Customer, CreateCustomerData } from '../types/customer';
import { Organization } from '../types/organization';
import {
  PlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingOffice2Icon,
  UserIcon,
  UserGroupIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  DocumentIcon,
  DocumentTextIcon,
  PaperClipIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentCheckIcon,
  DocumentChartBarIcon,
  EyeIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { Tooltip } from '../components/common/Tooltip';
import SearchBar from '../components/common/SearchBar';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/common/Modal';
import { formatDocumentDate } from 'utils/formatters';
import Pagination from 'components/common/Pagination';
import ConfirmDeleteModal from 'components/common/ConfirmDeleteModal';
import CustomerViewModal from 'components/customers/CustomerViewModal';
import CustomerList from 'components/customers/CustomerList';
import { customerToKeyValuePairs } from '../utils/customerDataMapper';
import DocumentViewerModal from 'components/documents/DocumentViewerModal';
import DocumentGeneratorModal from 'components/documents/DocumentGeneratorModal';
import { exportCustomerToExcel, exportAllCustomersToExcel } from '../utils/excelExport';
import BulkOrganizationAssignment from 'components/customers/BulkOrganizationAssignment';
import ConsultantDetailView from 'components/consultants/ConsultantDetailView';
import { getConsultantById } from '../services/consultantService';
import { Consultant } from '../types/consultant';

export interface SimpleOrganization {
  id: string;
  name: string;
}

const Customers: React.FC = () => {
  const { t } = useLanguage();
  const { customers, loading, createCustomer, updateCustomer, deleteCustomer } = useCustomers();
  const { organizations } = useOrganizations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isDocumentGeneratorOpen, setIsDocumentGeneratorOpen] = useState(false);
  const [customerWithDocuments, setCustomerWithDocuments] = useState<Customer | null>(null);
  const [customerForDocGen, setCustomerForDocGen] = useState<Customer | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [isInSelectMode, setIsInSelectMode] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showGasOnly, setShowGasOnly] = useState(false);
  const [isBulkOrgAssignmentOpen, setIsBulkOrgAssignmentOpen] = useState(false);
  const [selectedConsultantId, setSelectedConsultantId] = useState<string | null>(null);
  const [isConsultantViewOpen, setIsConsultantViewOpen] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Debug információk
  console.log('🏠 Customers component: Rendering with:', {
    customersCount: customers.length,
    loading: loading,
    customersArray: customers,
    firstCustomer: customers[0],
    organizationsCount: organizations.length
  });

  const handleCreateCustomer = async (data: CreateCustomerData) => {
    try {
      await createCustomer(data);
      setIsModalOpen(false);
      toast.success(t('common.messages.customer.createdSuccess'));
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error(t('common.messages.customer.createdError'));
    }
  };

  const handleEditCustomer = async (data: CreateCustomerData) => {
    if (!selectedCustomer) return;

    try {
      await updateCustomer(selectedCustomer.id, data);
      setIsModalOpen(false);
      setSelectedCustomer(null);
      toast.success(t('common.messages.customer.updatedSuccess'));
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error(t('common.messages.customer.updatedError'));
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    try {
      await deleteCustomer(customer.id);
      toast.success(t('common.messages.customer.deletedSuccess'));
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(t('common.messages.customer.deletedError'));
    }
  };

  const handleStatusChange = async (customer: Customer) => {
    try {
      const newStatus = customer.status === 'active' ? 'inactive' : 'active';
      await updateCustomer(customer.id, {
        ...customer,
        status: newStatus
      });
      toast.success(`${t('common.messages.customer.customerStatusUpdated')} ${customer.status === 'active' ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Error updating customer status:', error);
      toast.error(t('common.messages.customer.customerStatusUpdateFailed'));
    }
  };

  const openEditModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  // Function to open the document viewer modal
  const openDocumentModal = (customer: Customer) => {
    if (customer.documents && customer.documents.length > 0) {
      setCustomerWithDocuments(customer);
      setIsDocumentModalOpen(true);
    } else {
      toast.error(t('customers.messages.noDocuments') || 'No documents available');
    }
  };

  const openViewModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsViewModalOpen(true);
  };

  const handleGenerateDocument = (customer: Customer) => {
    setCustomerForDocGen(customer);
    setIsDocumentGeneratorOpen(true);
  };

  // Selection handlers
  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomers(prev => 
      prev.find(c => c.id === customer.id) 
        ? prev.filter(c => c.id !== customer.id)
        : [...prev, customer]
    );
  };

  const handleSelectAll = () => {
    setSelectedCustomers(selectedCustomers.length === currentCustomers.length ? [] : currentCustomers);
  };

  const handleAdvancedSearchToggle = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const handleBulkOrganizationAssignment = async (customerIds: string[], organizationIds: string[]) => {
    try {
      // Update each customer with the new organizations
      const updatePromises = customerIds.map(async (customerId) => {
        const customer = customers.find(c => c.id === customerId);
        if (!customer) return;

        const currentOrgs = customer.organizations || [];
        // Add new organizations without duplicates
        const newOrgs = Array.from(new Set([...currentOrgs, ...organizationIds]));

        await updateCustomer(customerId, {
          ...customer,
          organizations: newOrgs
        });
      });

      await Promise.all(updatePromises);
      
      toast.success(`${customerIds.length} ügyfélhez ${organizationIds.length} szervezet hozzárendelve`);
      setSelectedCustomers([]);
      setIsInSelectMode(false);
    } catch (error) {
      console.error('Error in bulk organization assignment:', error);
      toast.error('Hiba a szervezetek hozzárendelése során');
    }
  };

  const handleRefresh = () => {
    // This will trigger a re-fetch of customers
    // The useCustomers hook should handle the refresh
    console.log('Refreshing customers...');
  };

  const handleConsultantClick = async (consultantId: string) => {
    try {
      const consultant = await getConsultantById(consultantId);
      if (consultant) {
        setSelectedConsultant(consultant);
        setSelectedConsultantId(consultantId);
        setIsConsultantViewOpen(true);
      } else {
        toast.error('Szakreferens nem található');
      }
    } catch (error) {
      console.error('Error loading consultant:', error);
      toast.error('Hiba a szakreferens betöltése során');
    }
  };

  // Szűrés kezelése
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      // Név vagy email alapján szűrés
      const searchMatch =
        (customer.type === 'organization' ? customer.companyName : `${customer.firstName} ${customer.lastName}`)
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Ügyfélazonosító keresés
        customer.customerCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Adószám és cégjegyzékszám keresés
        customer.taxNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Cím elemek keresése
        customer.address?.street?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.postalCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Teljes cím keresése (ha van)
        customer.companyAddress?.toLowerCase().includes(searchTerm.toLowerCase());

      // Szervezet alapján szűrés
      const orgMatch = selectedOrgs.length === 0 ||
        customer.organizations?.some(org => selectedOrgs.includes(org));

      // Gáz ügyfél szűrés
      const gasMatch = !showGasOnly || customer.isGasCustomer === true;

      return searchMatch && orgMatch && gasMatch;
    });
  }, [customers, searchTerm, selectedOrgs, showGasOnly]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const exportToCSV = () => {
    // CSV fejléc
    const headers = [
      'Type',
      'Company Name',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Tax Number',
      'Organizations',
      'Status',
      'Notes',
      'Address',
      'Contact Person'
    ].join(',');

    // CSV sorok generálása
    const csvRows = filteredCustomers.map(customer => {
      const address = `${customer.address.street}, ${customer.address.city}, ${customer.address.postalCode}, ${customer.address.country}`;
      const contactPerson = `${customer.contactPerson.name} (${customer.contactPerson.email}, ${customer.contactPerson.phone}, ${customer.contactPerson.position})`;

      // A szervezetek nevének összegyűjtése
      const orgNames = customer.organizations
        ?.map(orgId => {
          const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
          return org?.name;
        })
        .filter((name): name is string => Boolean(name))
        .join('; ') || '';

      return [
        customer.type,
        customer.companyName || '',
        customer.firstName || '',
        customer.lastName || '',
        customer.email,
        customer.phone || '',
        customer.taxNumber || '',
        orgNames,
        customer.status,
        customer.notes || '',
        address,
        contactPerson
      ].map(field => `"${field}"`).join(',');
    });

    // CSV tartalom összeállítása
    const csvContent = [headers, ...csvRows].join('\n');

    // Fájl létrehozása és letöltése
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `customers_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleOrganizationUpdate = async (customer: Customer, orgId: string, action: 'add' | 'remove') => {
    try {
      const currentOrgs = customer.organizations || [];
      const newOrgs = action === 'add'
        ? [...currentOrgs, orgId]
        : currentOrgs.filter(id => id !== orgId);

      await updateCustomer(customer.id, {
        ...customer,
        organizations: newOrgs
      });
      toast.success(t('common.messages.customer.organizationsUpdated'));
    } catch (error) {
      console.error('Error updating organizations:', error);
      toast.error(t('common.messages.customer.organizationsUpdateFailed'));
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4 max-w-[100vw] px-2 sm:px-0 overflow-x-hidden">
      {/* Header */}
      <CommonPageHeader
        title={t('customers.title')}
        subtitle="Ügyfelek kezelése és kapcsolatok"
        icon={UserGroupIcon}
        onRefresh={handleRefresh}
        showLastUpdate={false}
      >
        {isInSelectMode && selectedCustomers.length > 0 && (
          <button
            onClick={() => setIsBulkOrgAssignmentOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-green-500/90 backdrop-blur-sm text-white rounded-lg hover:bg-green-600/90 transition-colors"
          >
            <BuildingOffice2Icon className="w-5 h-5 mr-2" />
            <span>Szervezet hozzárendelése ({selectedCustomers.length})</span>
          </button>
        )}
        <button
          onClick={() => {
            if (isInSelectMode) {
              setIsInSelectMode(false);
              setSelectedCustomers([]);
            } else {
              setIsInSelectMode(true);
            }
          }}
          className={`inline-flex items-center px-4 py-2 backdrop-blur-sm text-white rounded-lg transition-colors ${
            isInSelectMode 
              ? 'bg-red-500/90 hover:bg-red-600/90' 
              : 'bg-white/20 hover:bg-white/30'
          }`}
        >
          {isInSelectMode ? (
            <>
              <XMarkIcon className="w-5 h-5 mr-2" />
              <span>Kiválasztás vége</span>
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-5 h-5 mr-2" />
              <span>Tömeges kiválasztás</span>
            </>
          )}
        </button>
        <button
          onClick={() => {
            try {
              exportAllCustomersToExcel(customers);
              toast.success(`${customers.length} ügyfél exportálva Excel fájlba`);
            } catch (error) {
              console.error('Excel export error:', error);
              toast.error('Hiba az Excel export során');
            }
          }}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
          <span>Összes exportálása</span>
        </button>
        <button
          onClick={() => { setSelectedCustomer(null); setIsModalOpen(true); }}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          {t('customers.new')}
        </button>
      </CommonPageHeader>

      {/* Szűrési lehetőségek - görgethetők mobilon */}
      <div className="space-y-2 sm:space-y-3">
        <div className="w-full">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Keresés név, email, ügyfélazonosító, adószám, cégjegyzékszám vagy cím alapján..."
          />
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0">
          <button
            onClick={() => setSelectedOrgs([])}
            className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
              ${selectedOrgs.length === 0
                ? 'border-primary-500 bg-primary-100 text-primary-700'
                : 'border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
          >
            <BuildingOffice2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            {t('customers.fields.organizations')}
          </button>
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => {
                setSelectedOrgs(prev =>
                  prev.includes(org.id)
                    ? prev.filter(id => id !== org.id)
                    : [...prev, org.id]
                );
              }}
              className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
                ${selectedOrgs.includes(org.id)
                  ? 'border-primary-500 bg-primary-100 text-primary-700'
                  : 'border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
            >
              <BuildingOffice2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="max-w-[120px] sm:max-w-[150px] truncate">{org.name}</span>
            </button>
          ))}
          <button
            onClick={() => setShowGasOnly(prev => !prev)}
            className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
              ${showGasOnly
                ? 'border-orange-500 bg-orange-100 text-orange-700'
                : 'border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
          >
            <FireIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            {t('gas.customer.gasCustomersFilter')}
          </button>
        </div>
      </div>

      <CustomerList
        customers={currentCustomers}
        organizations={organizations}
        onStatusChange={handleStatusChange}
        onViewClick={openViewModal}
        onEditClick={openEditModal}
        onDeleteClick={(customer) => {
          setCustomerToDelete(customer);
          setIsConfirmDeleteOpen(true);
        }}
        onDocumentClick={openDocumentModal}
        onGenerateDocument={handleGenerateDocument}
        onView={openViewModal}
        onEdit={openEditModal}
        onDelete={(customer) => {
          setCustomerToDelete(customer);
          setIsConfirmDeleteOpen(true);
        }}
        selectedCustomers={selectedCustomers}
        onSelectCustomer={handleSelectCustomer}
        onSelectAll={handleSelectAll}
        isInSelectMode={isInSelectMode}
        showAdvancedSearch={showAdvancedSearch}
        searchTerm={searchTerm}
        onAdvancedSearchToggle={handleAdvancedSearchToggle}
        onRefresh={handleRefresh}
        onConsultantClick={handleConsultantClick}
      />

      {/* Pagination controls */}
      <div className="relative z-10">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filteredCustomers.length}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
        />
      </div>

      <CustomerViewModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        customer={selectedCustomer}
        organizations={organizations}
        onEdit={openEditModal}
      />

      <CustomerForm
        isOpen={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedCustomer(null);
        }}
        onSubmit={selectedCustomer ? handleEditCustomer : handleCreateCustomer}
        initialData={selectedCustomer}
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={isDocumentModalOpen}
        onClose={() => setIsDocumentModalOpen(false)}
        customerWithDocuments={customerWithDocuments}
      />

      {/* Dokumentum generáló modal */}
      <DocumentGeneratorModal
        isOpen={isDocumentGeneratorOpen}
        onClose={() => setIsDocumentGeneratorOpen(false)}
        documentDataProvider={customerForDocGen}
      />

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setCustomerToDelete(null);
        }}
        onConfirm={() => {
          if (customerToDelete) {
            handleDeleteCustomer(customerToDelete);
          }
          setIsConfirmDeleteOpen(false);
          setCustomerToDelete(null);
        }}
        title={t('customers.messages.confirmTitle')}
        deleteMessage={customerToDelete ?
          `${t('customers.messages.deleteQuestion')} "${customerToDelete.type === 'organization' ? customerToDelete.companyName : `${customerToDelete.firstName} ${customerToDelete.lastName}`}"?` :
          t('customers.messages.deleteQuestion')}
        deleteWarning={t('customers.messages.deleteWarning')}
      />

      {/* Bulk Organization Assignment Modal */}
      <BulkOrganizationAssignment
        isOpen={isBulkOrgAssignmentOpen}
        onClose={() => setIsBulkOrgAssignmentOpen(false)}
        selectedCustomers={selectedCustomers}
        organizations={organizations}
        onAssign={handleBulkOrganizationAssignment}
      />

      {/* Consultant Detail View Modal */}
      {selectedConsultant && (
        <Modal
          isOpen={isConsultantViewOpen}
          onClose={() => {
            setIsConsultantViewOpen(false);
            setSelectedConsultantId(null);
            setSelectedConsultant(null);
          }}
          title="Szakreferens részletei"
          size="6xl"
        >
          <ConsultantDetailView
            consultant={selectedConsultant}
            onClose={() => {
              setIsConsultantViewOpen(false);
              setSelectedConsultantId(null);
              setSelectedConsultant(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default Customers;